import { useEffect, useState } from 'react';

import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import { getFormDefaultValue, getTableHeaders } from '../../utils/forms/FormDataModel';
import { ContentItem, OnAddArguments, TabContent } from '../../utils/forms/FormInterface';
import AbstractForm from '../Entity/AbstractForm';
import AbstractLanding from '../Entity/AbstractLanding';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import {
  HOMEPAGE_SERVICE_CATEGORY_ENTITY,
  SERVICE_CARDS_MANAGEMENT,
} from '../../constants/EntityName';
import { serviceCardsFormData } from '../../datamodels/ServiceCards';
import callApi from '../../Api/config';

const ServiceCardsManagement = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const [tableHeaders, setTableHeaders] = useState<any[]>(() =>
    getTableHeaders(serviceCardsFormData)
  );
  const [categoryFilterOptions, setCategoryFilterOptions] = useState<any[]>([]);

  const defaultFormValue: any = getFormDefaultValue(serviceCardsFormData);
  const addName = 'Service Card';
  const headerName = 'All Services';
  const categoryDropdownConfig = [{ name: HOMEPAGE_SERVICE_CATEGORY_ENTITY, getAllResponse: true }];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await callApi(HOMEPAGE_SERVICE_CATEGORY_ENTITY, 'GET', null, {
          page: 1,
          limit: 1000,
        });

        if (response?.status) {
          const categories = response?.data?.data?.results || response?.data?.results || [];
          const filters = categories.map((category: any) => ({
            text: category.name,
            value: category.name,
          }));
          setCategoryFilterOptions(filters);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch service categories', error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    setTableHeaders(prev =>
      prev.map(header =>
        header.tableKey === 'heading' ? { ...header, filterOptions: categoryFilterOptions } : header
      )
    );
  }, [categoryFilterOptions]);

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
    }
    // Immediately update UI
    setRefreshData(prev => !prev);
    // Close the tab after successful update
    onTabClick('0');

    // Force a re-fetch after a short delay to ensure backend sync
    setTimeout(() => {
      setRefreshData(prev => !prev);
    }, 100);
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const checkArr = content.filter((item: ContentItem) => item.key === argument1.id);
      checkArr.length ? null : onAdd(argument1);
    } else {
      setShowFormComponent(true);
      setUserName('Add Service Card');
      onTabClick('a');
    }
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    key === 'a' && setShowFormComponent(false);
  };

  const onAdd = (argument1: OnAddArguments) => {
    const newObj = {
      key: argument1.id,
      tabTitle: argument1.id,
      content: (
        <GenericAccordian key={content.length}>
          <AbstractForm
            entityId={argument1.id}
            isCreateMode={false}
            headerName={headerName}
            entityName={SERVICE_CARDS_MANAGEMENT}
            formTemplate={serviceCardsFormData}
            formDefaultPayload={getFormDefaultValue(serviceCardsFormData)}
            onSuccessfulUpdate={onSuccessful}
            formHeader=""
            showDeleteDraft={true}
            closeTab={closeTab}
            additionalEntityName={categoryDropdownConfig}
          />
        </GenericAccordian>
      ),
    };

    // It is to make the newly added tab as active
    setContent((prev: any) => [...prev, newObj]);
    argument1.id && setActiveKey(String(argument1.id));
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        onTabClick('0');
      } else {
        onTabClick('0');
        // whenever a tab is closed, that tab is filtered out from the list of tabs which are rendered inside the AbstractTab component
        setContent(content => content.filter(item => String(item.key) !== targetKey));
      }
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Service Cards Management"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent}
      content={content}
      formComponent={
        <GenericAccordian>
          <AbstractForm
            isCreateMode={true}
            headerName={headerName}
            entityName={SERVICE_CARDS_MANAGEMENT}
            formTemplate={serviceCardsFormData}
            formDefaultPayload={defaultFormValue}
            onSuccessfulUpdate={onSuccessful}
            closeTab={closeTab}
            additionalEntityName={categoryDropdownConfig}
          />
        </GenericAccordian>
      }
    >
      <AbstractLanding
        headerName={headerName}
        addName={addName}
        entityName={SERVICE_CARDS_MANAGEMENT}
        tableHeaders={tableHeaders}
        onAdd={onAddNew}
        refreshData={refreshData}
      />
    </AbstractTabs>
  );
};

export default ServiceCardsManagement;
